// Support Center articles. Each entry is a full, SEO-optimised help
// article: keyword-rich intro, deep H2/H3 structure, actionable steps,
// examples, common mistakes, best practices and a Frequently Asked
// Questions section. Content is grounded in what Reality Twin actually
// does today — no fabricated features.

export type ArticleSection =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "code"; lang?: string; text: string }
  | { type: "callout"; kind?: "info" | "warn"; text: string };

export interface SupportArticle {
  slug: string;
  category: string;
  title: string;
  summary: string;
  updated: string; // ISO date
  readMinutes: number;
  body: ArticleSection[];
}

export const SUPPORT_ARTICLES: SupportArticle[] = [
  // ── Getting started ─────────────────────────────────────────────
  {
    slug: "quickstart",
    category: "Getting started",
    title: "Quickstart: launch your first AI Twin in 60 seconds",
    summary:
      "The fastest way to set up an AI Twin for your business — sign up, connect your website, and go live with a cited, on-brand assistant.",
    updated: "2026-06-20",
    readMinutes: 7,
    body: [
      {
        type: "p",
        text: "Reality Twin turns your public website into a conversational AI Twin — a business-grade AI assistant that answers visitor questions with citations, qualifies leads, and hands off to your team when it matters. This quickstart takes you from signup to a live twin embedded on your site in about a minute, without writing a line of code.",
      },
      {
        type: "p",
        text: "You will need: a work email address, the root URL of the website you want your AI Twin to learn from, and access to your site template so you can paste one script tag. That's it — no engineering time, no infrastructure, no model tuning.",
      },

      { type: "h2", text: "Step 1 — Create your Reality Twin account" },
      {
        type: "ol",
        items: [
          "Open https://realitytwin.io/auth and choose Sign up.",
          "Enter your work email and a password, or continue with Google.",
          "Confirm your email if prompted — the confirmation link is valid for 24 hours.",
          "You land directly on the onboarding screen with one input: your website URL.",
        ],
      },
      {
        type: "p",
        text: "The free plan covers 100 conversations per month, which is enough to evaluate the product on a real site. You can upgrade later without losing any data — see the article Compare Free, Starter, Pro and Enterprise.",
      },

      { type: "h2", text: "Step 2 — Connect your website" },
      {
        type: "p",
        text: "Paste your root URL (for example https://yourcompany.com) into the onboarding field. Reality Twin's crawler starts immediately: it follows internal links from the root, obeys robots.txt, extracts the visible text of each page, and builds a citable knowledge graph.",
      },
      {
        type: "callout",
        kind: "info",
        text: "Typical crawl time is 30–90 seconds for a marketing site with fewer than 100 pages. Sites with 500+ pages can take a few minutes; the crawl runs in the background so you can keep configuring your twin.",
      },
      { type: "h3", text: "What the crawler indexes" },
      {
        type: "ul",
        items: [
          "HTML pages linked from your root URL, up to your plan's page limit.",
          "Page titles, meta descriptions, headings, and body copy.",
          "Anchor text and internal link structure (used to build topical relationships).",
        ],
      },
      { type: "h3", text: "What it skips by design" },
      {
        type: "ul",
        items: [
          "Anything disallowed by robots.txt or blocked by your WAF.",
          "Login-gated content, admin routes, and query-string duplicates.",
          "Binary files (images, video). Upload PDFs and DOCX separately — see Upload PDFs, DOCX and Markdown.",
        ],
      },

      { type: "h2", text: "Step 3 — Chat with your AI Twin" },
      {
        type: "p",
        text: "The dashboard opens on your new twin the moment indexing completes. Ask it questions your website already answers: pricing, product features, comparison against a competitor, opening hours, warranty terms. Every reply is grounded in the pages the crawler read, with clickable citations that jump straight to the source.",
      },
      {
        type: "p",
        text: "If an answer looks wrong, the fix is almost always at the source — see Fix wrong or outdated answers. Never edit response text directly.",
      },

      { type: "h2", text: "Step 4 — Embed the chat widget" },
      {
        type: "p",
        text: "Copy the embed snippet from the Twin screen and paste it before the closing </body> tag in any page template. The widget loads a floating chat bubble anchored to the bottom-right corner by default.",
      },
      {
        type: "code",
        lang: "html",
        text: `<script async src="https://realitytwin.io/api/public/embed/YOUR_COMPANY_ID"></script>`,
      },
      {
        type: "p",
        text: "For framework-specific instructions (Next.js, WordPress, Shopify, Webflow, HubSpot CMS), see Embed the chat widget on any page.",
      },

      { type: "h2", text: "Step 5 — Verify the widget is live" },
      {
        type: "ol",
        items: [
          "Open your site in a private browser window.",
          "Confirm the chat bubble appears within 2 seconds.",
          "Send a test message — the reply should include a citation you can click.",
          "Return to the Reality Twin dashboard and confirm the test conversation appears under Conversations.",
        ],
      },
      {
        type: "callout",
        kind: "warn",
        text: "If the bubble does not appear, check ad-blockers, Content Security Policy, and your allow-listed domains. Full checklist in Widget not appearing on my site.",
      },

      { type: "h2", text: "What to do next" },
      {
        type: "ul",
        items: [
          "Enrich knowledge — upload PDFs, product spec sheets and internal FAQs.",
          "Tune personality — set voice, tone, verbosity, and topic boundaries.",
          "Configure lead scoring — decide which conversations reach your inbox in real time.",
          "Connect your CRM — push qualified leads into HubSpot or Salesforce.",
          "Publish your marketplace profile so other twins can discover yours.",
        ],
      },

      { type: "h2", text: "Common mistakes to avoid" },
      {
        type: "ul",
        items: [
          "Pointing the crawler at a staging domain — always use the URL your visitors see.",
          "Leaving outdated pricing PDFs online after publishing new ones — delete stale versions or the twin may cite them.",
          "Skipping personality tuning — the default voice is neutral, which rarely matches a strong brand.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "How long does it take to set up an AI Twin?" },
      {
        type: "p",
        text: "For a typical marketing site with under 100 pages, expect 60–90 seconds from paste-URL to first reply. Larger sites index in a few minutes; the widget snippet takes another minute to install.",
      },
      { type: "h3", text: "Do I need developers to launch a Reality Twin?" },
      {
        type: "p",
        text: "No. Setup is a URL paste and a single script tag. Developers only get involved if you want deeper integrations — custom CRM fields, Content Security Policy, or the REST API.",
      },
      { type: "h3", text: "Can I try Reality Twin before adding a credit card?" },
      {
        type: "p",
        text: "Yes. The free plan covers 100 conversations per month and does not require a card. Upgrade only when you're ready to scale.",
      },
      { type: "h3", text: "What happens to my data if I cancel?" },
      {
        type: "p",
        text: "Your twin keeps running until the end of the billing period, then switches to read-only. Data stays available for export for 30 days before deletion — see Change or cancel your plan.",
      },
    ],
  },
  {
    slug: "connect-website",
    category: "Getting started",
    title: "Connect your website: how the AI Twin crawler works",
    summary:
      "How Reality Twin's crawler fetches your site, what it indexes, and how to control exactly what your AI Twin learns.",
    updated: "2026-06-22",
    readMinutes: 8,
    body: [
      {
        type: "p",
        text: "The website connection is the first and most important knowledge source for any Reality Twin AI Twin. This guide explains exactly how the crawler works, which pages it can and cannot see, and how to control what ends up in your twin's knowledge graph — so your visitors get accurate answers grounded in the pages you approve.",
      },

      { type: "h2", text: "How the crawler works" },
      {
        type: "p",
        text: "Reality Twin's crawler behaves like a well-mannered search-engine bot. It identifies itself as RealityTwinBot, respects robots.txt, honours rate limits, and follows internal links from the root URL you provide. Every crawled page is parsed into structured chunks and embedded into your workspace's knowledge graph.",
      },
      { type: "h3", text: "The crawl lifecycle" },
      {
        type: "ol",
        items: [
          "You submit a root URL from onboarding or Knowledge → Sources.",
          "The crawler fetches robots.txt and builds an allow-list for your host.",
          "It requests the root page, extracts links, and enqueues them.",
          "Each page is rendered (with JavaScript, 10-second budget), parsed and chunked.",
          "Chunks are embedded and stored in your workspace — private to you.",
        ],
      },

      { type: "h2", text: "What the crawler fetches" },
      {
        type: "ul",
        items: [
          "HTML pages linked from your root, up to your plan's page cap.",
          "Meta title, meta description, canonical URL and language.",
          "Headings (h1–h4), body copy, lists, tables, and visible link anchor text.",
          "Structured data (JSON-LD) — used as extra signal, not as authoritative content.",
        ],
      },

      { type: "h2", text: "What the crawler intentionally skips" },
      {
        type: "ul",
        items: [
          "URLs disallowed by robots.txt for user-agent RealityTwinBot or *.",
          "Login-gated content — the crawler does not authenticate.",
          "Admin, wp-admin, checkout, and similar sensitive paths.",
          "Binary assets (images, video, audio). Upload documents separately.",
          "Duplicate URLs with tracking parameters (utm_*, gclid, fbclid).",
        ],
      },

      { type: "h2", text: "Control what gets indexed" },
      { type: "h3", text: "Option 1: robots.txt" },
      {
        type: "p",
        text: "Add a targeted rule for our crawler user-agent. This is the recommended approach for permanent exclusions.",
      },
      {
        type: "code",
        lang: "text",
        text: `User-agent: RealityTwinBot
Disallow: /internal/
Disallow: /drafts/
Allow: /`,
      },
      { type: "h3", text: "Option 2: dashboard toggles" },
      {
        type: "p",
        text: "Open Knowledge → Sources → your website. Every indexed page is listed with a visibility toggle. Turn a page off and it is removed from retrieval within seconds — no re-crawl required.",
      },
      { type: "h3", text: "Option 3: meta noindex" },
      {
        type: "p",
        text: 'The crawler honours <meta name="robots" content="noindex"> — useful for individual pages you want out of both search and your twin.',
      },

      { type: "h2", text: "Submit a sitemap for large sites" },
      {
        type: "p",
        text: "For sites over 200 pages, submit a sitemap.xml URL under Knowledge → Sources → Add sitemap. The crawler uses it as an authoritative list of URLs, guaranteeing no page is missed because it is only reachable through search or JavaScript navigation.",
      },
      {
        type: "callout",
        kind: "info",
        text: "Sitemaps also speed up incremental re-crawls: the crawler compares lastmod dates and only re-fetches pages that changed.",
      },

      { type: "h2", text: "Refresh cadence and manual re-crawls" },
      {
        type: "p",
        text: "By default the crawler re-runs weekly. You can trigger a manual re-crawl any time — see Force a re-crawl of your website. Enterprise plans support daily and event-driven re-crawls fired by CMS webhooks.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Use a single canonical domain (either www or apex) to avoid duplicate indexing.",
          "Keep your pricing on one page — not spread across several — so citations are unambiguous.",
          "Fix broken internal links; unreachable pages never enter the graph.",
          "Publish product specs as HTML pages where possible; PDF is fine but text HTML is easier to update.",
        ],
      },

      { type: "h2", text: "Common mistakes" },
      {
        type: "ul",
        items: [
          "Blocking the crawler in Cloudflare or a WAF then wondering why the twin knows nothing.",
          "Pointing at a staging URL — the twin will happily learn from unfinished copy.",
          "Leaving Disallow: / from a legacy robots.txt — nothing gets indexed.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Does the crawler execute JavaScript?" },
      {
        type: "p",
        text: "Yes, with a 10-second budget per page. Content that only appears after a long client-side fetch may be missed — publish it as static HTML or upload a document.",
      },
      { type: "h3", text: "Can I connect multiple websites to one AI Twin?" },
      {
        type: "p",
        text: "Yes on Starter and above. Add each root URL as a separate source; they merge into one knowledge graph per twin.",
      },
      { type: "h3", text: "How often is my website re-crawled?" },
      {
        type: "p",
        text: "Weekly by default. Manual re-crawls are unlimited on all paid plans, and Enterprise plans support webhook-triggered incremental refreshes.",
      },
      { type: "h3", text: "Is the crawl private to my workspace?" },
      {
        type: "p",
        text: "Yes. Crawled content is stored per workspace and never used to train shared models. Read Where is my data stored? for full details.",
      },
    ],
  },
  {
    slug: "embed-widget",
    category: "Getting started",
    title: "Embed the AI Twin chat widget on any website",
    summary:
      "Install the Reality Twin chat widget with one script tag, then customise position, colour, open-state and framework integration.",
    updated: "2026-06-25",
    readMinutes: 7,
    body: [
      {
        type: "p",
        text: "The Reality Twin chat widget is a single async script tag that loads a floating chat bubble anchored to a corner of your page. It is fully self-contained: no iframe conflicts, no dependencies, no build step. This guide covers installation, customisation and framework-specific tips for WordPress, Shopify, Webflow, Next.js, HubSpot CMS and more.",
      },

      { type: "h2", text: "The base snippet" },
      {
        type: "p",
        text: "Find your Company ID on the Twin screen in the dashboard, then paste the snippet before the closing </body> tag on any page you want the widget to appear.",
      },
      {
        type: "code",
        lang: "html",
        text: `<script async
  src="https://realitytwin.io/api/public/embed/YOUR_COMPANY_ID"
  data-position="bottom-right"
  data-primary-color="#111827"
  data-open-on-load="false"
></script>`,
      },
      {
        type: "p",
        text: "The widget script is ~18 KB gzipped, loads asynchronously, and never blocks page rendering.",
      },

      { type: "h2", text: "Configuration options" },
      {
        type: "ul",
        items: [
          "data-position — bottom-right (default), bottom-left, or custom offsets with data-offset-x / data-offset-y.",
          "data-primary-color — any hex, matches your brand accent.",
          "data-open-on-load — set to true to open the panel automatically 3 seconds after page load.",
          "data-greeting — override the default greeting for this page (e.g. a pricing-page specific opener).",
          "data-context — a JSON blob passed to the twin as page context (page title, product SKU, user role).",
        ],
      },

      { type: "h2", text: "Framework-specific installation" },
      { type: "h3", text: "WordPress" },
      {
        type: "p",
        text: "Use a Custom HTML block in the footer widget area, or paste into your theme's footer.php before </body>. Popular header/footer plugins (WPCode, Insert Headers and Footers) also work.",
      },
      { type: "h3", text: "Shopify" },
      {
        type: "p",
        text: "Open Online Store → Themes → Edit code → theme.liquid. Paste the snippet directly before the closing </body> tag.",
      },
      { type: "h3", text: "Webflow" },
      { type: "p", text: "Project settings → Custom code → Footer code. Paste and publish." },
      { type: "h3", text: "Next.js (App Router)" },
      {
        type: "code",
        lang: "tsx",
        text: `import Script from "next/script";

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html>
      <body>
        {children}
        <Script
          src="https://realitytwin.io/api/public/embed/YOUR_COMPANY_ID"
          strategy="afterInteractive"
        />
      </body>
    </html>
  );
}`,
      },
      { type: "h3", text: "HubSpot CMS" },
      {
        type: "p",
        text: "Settings → Website → Pages → Site header and footer HTML → Site footer HTML.",
      },
      { type: "h3", text: "Squarespace / Wix" },
      {
        type: "p",
        text: "Both support custom footer code injection. Paste the snippet into the site-wide footer code block.",
      },

      { type: "h2", text: "Restrict where the widget can render" },
      {
        type: "p",
        text: "By default the widget only initialises on domains you allow-list. Open Twin → Settings → Domains and add your production and staging hostnames. This prevents the widget from appearing on scraped copies of your site.",
      },

      { type: "h2", text: "Content Security Policy (CSP)" },
      {
        type: "p",
        text: "If your site sends a strict CSP, add the following to allow the widget to load and connect:",
      },
      {
        type: "code",
        lang: "text",
        text: `script-src 'self' https://realitytwin.io;
connect-src 'self' https://realitytwin.io wss://realitytwin.io;
frame-src https://realitytwin.io;
img-src 'self' https://realitytwin.io data:;`,
      },

      { type: "h2", text: "Programmatic control" },
      { type: "p", text: "The widget exposes a global window.RealityTwin object once loaded:" },
      {
        type: "code",
        lang: "javascript",
        text: `window.RealityTwin.open();
window.RealityTwin.close();
window.RealityTwin.setContext({ user: { id: "u_123", plan: "pro" } });
window.RealityTwin.on("lead", (lead) => console.log(lead));`,
      },

      { type: "h2", text: "Accessibility" },
      {
        type: "p",
        text: "The widget ships with keyboard navigation, ARIA labels, focus trapping inside the open panel, and honours prefers-reduced-motion. Contrast is verified against WCAG AA at every colour you configure.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Does the widget slow down my site?" },
      {
        type: "p",
        text: "No. It loads async after the main page paint, adds ~18 KB gzipped, and has no impact on Core Web Vitals in our own tests.",
      },
      { type: "h3", text: "Can I hide the widget on certain pages?" },
      {
        type: "p",
        text: "Yes — either omit the script tag on those pages, or set data-hide-on to a comma-separated list of URL patterns.",
      },
      { type: "h3", text: "Can I use my own chat UI instead of the widget?" },
      {
        type: "p",
        text: "Yes on Pro and above. Use the /api/v1/chat REST endpoint to build a fully custom UI while Reality Twin handles the AI.",
      },
      { type: "h3", text: "Does the widget appear on mobile?" },
      {
        type: "p",
        text: "Yes, with a mobile-optimised layout that expands to full-screen when opened.",
      },
    ],
  },
  {
    slug: "invite-teammates",
    category: "Getting started",
    title: "Invite teammates and manage workspace roles",
    summary:
      "Add colleagues to your Reality Twin workspace, assign granular roles, and manage access, MFA and SSO for enterprise teams.",
    updated: "2026-06-24",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Reality Twin workspaces are built for teams. Invite colleagues, share ownership of your AI Twin, review conversations together, and split responsibility between marketing, sales and operations with role-based access control.",
      },

      { type: "h2", text: "Invite a teammate" },
      {
        type: "ol",
        items: [
          "Open Settings → Team.",
          "Click Invite teammate and enter their work email.",
          "Select a role (Admin, Editor or Viewer).",
          "Optional: attach a personal note that appears in the invitation email.",
          "Send. They receive a signup link valid for 7 days.",
        ],
      },

      { type: "h2", text: "Roles and permissions" },
      { type: "h3", text: "Admin" },
      {
        type: "p",
        text: "Full access. Manages billing, team members, integrations, security settings and workspace deletion. There must be at least one Admin per workspace.",
      },
      { type: "h3", text: "Editor" },
      {
        type: "p",
        text: "Can edit knowledge sources, personality, boundaries, integrations and the marketplace profile. Cannot manage billing or team.",
      },
      { type: "h3", text: "Viewer" },
      {
        type: "p",
        text: "Read-only access to conversations, leads and analytics. Ideal for stakeholders, execs, or external agencies who need visibility without edit rights.",
      },

      { type: "h2", text: "Security requirements" },
      {
        type: "ul",
        items: [
          "MFA is required for all operator accounts. First sign-in prompts for TOTP setup.",
          "Google Workspace sign-in inherits your Workspace 2SV.",
          "Enterprise plans support SAML SSO with the identity provider of your choice (Okta, Azure AD, Google, JumpCloud).",
          "SCIM 2.0 provisioning is available on Enterprise for automated onboarding and off-boarding.",
        ],
      },

      { type: "h2", text: "Removing a teammate" },
      {
        type: "p",
        text: "Settings → Team → the row → Remove. Their sessions are invalidated within 60 seconds; API keys they created are revoked; conversations they handled remain in the workspace with their name preserved for audit history.",
      },

      { type: "h2", text: "Multiple workspaces" },
      {
        type: "p",
        text: "Each Reality Twin account can belong to any number of workspaces. Switch from the top-left workspace picker. Common patterns:",
      },
      {
        type: "ul",
        items: [
          "One workspace per legal entity or brand.",
          "Separate production and staging workspaces to try changes safely.",
          "Agencies: one workspace per client, invited from the agency's parent account.",
        ],
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Use group email addresses for Admins (admin@ or ops@) so no single person is a lock-out risk.",
          "Grant Viewer, not Editor, to execs who only need dashboards.",
          "Rotate teammates off the workspace when they change roles internally.",
          "Turn on SSO + SCIM as soon as your team hits 10 people — it removes manual off-boarding.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "How many teammates can I invite?" },
      {
        type: "p",
        text: "Free plans include 2 seats. Starter includes 5, Pro includes 20. Enterprise is unlimited. Additional seats are available on Starter and Pro at a per-seat price shown on the pricing page.",
      },
      { type: "h3", text: "Can teammates have different access to different twins?" },
      { type: "p", text: "Yes on Pro and above. Per-twin roles override workspace roles." },
      { type: "h3", text: "Does removing a teammate delete their conversations?" },
      {
        type: "p",
        text: "No. Conversations belong to the workspace; only the person's access is removed.",
      },
      { type: "h3", text: "Can external contractors join without a full seat?" },
      { type: "p", text: "Yes — invite them as a Viewer, which is included in all paid plans." },
    ],
  },

  // ── Knowledge & training ────────────────────────────────────────
  {
    slug: "knowledge-sources",
    category: "Knowledge & training",
    title: "What knowledge sources can an AI Twin ingest?",
    summary:
      "Every source type Reality Twin can ingest today — your website, uploaded documents (PDF, DOCX, Markdown, CSV and more) and pasted text — plus plan limits and what is coming next.",
    updated: "2026-06-18",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Your AI Twin only knows what you feed it. Reality Twin supports several source types, all indexed into one unified knowledge graph per twin. This guide covers every currently supported source, how they interact, and how to combine them for the most accurate answers.",
      },

      { type: "h2", text: "Sources available today" },
      { type: "h3", text: "Website crawl" },
      {
        type: "p",
        text: "The default source. Point at a root URL, we crawl and index — see Connect your website: how the AI Twin crawler works.",
      },
      { type: "h3", text: "Documents (PDF, DOCX, MD, TXT, CSV)" },
      {
        type: "p",
        text: "Drag-and-drop upload for the material your website doesn't publish — spec sheets, price lists, policies, internal SOPs. Full details in Upload PDFs, DOCX and Markdown.",
      },
      { type: "h3", text: "Structured Q&A" },
      {
        type: "p",
        text: "Canonical question-and-answer pairs written directly in the dashboard. They rank highest in retrieval, so use them to overrule ambiguous or contradictory sources.",
      },
      { type: "h3", text: "Pasted text" },
      {
        type: "p",
        text: "Paste any text straight into the knowledge base — policies, FAQs, a price list, notes from a call. Useful for knowledge that lives in someone’s head rather than on a page.",
      },
      { type: "h3", text: "Sitemap.xml import" },
      { type: "p", text: "For very large sites, supply a sitemap URL so no page is missed." },

      { type: "h2", text: "Plan limits" },
      {
        type: "ul",
        items: [
          "Free — website only, up to 100 pages.",
          "Starter — website up to 1,000 pages + 10 uploaded documents + unlimited Q&A.",
          "Pro — unlimited pages and documents.",
          "Enterprise — all of the above plus Google Drive, Confluence, Zendesk (see below).",
        ],
      },

      { type: "h2", text: "Coming soon" },
      {
        type: "p",
        text: "The following sources are on the near-term roadmap and available in private beta for Enterprise customers — talk to sales to opt in:",
      },
      {
        type: "ul",
        items: [
          "Google Drive (Docs, Sheets, Slides).",
          "Confluence Cloud and Data Center.",
          "Zendesk Help Center articles.",
          "Salesforce Knowledge base.",
          "Slack channels (opt-in per channel, private).",
        ],
      },

      { type: "h2", text: "How the sources combine" },
      {
        type: "p",
        text: "All sources feed one knowledge graph. When two sources disagree, retrieval prefers, in order:",
      },
      {
        type: "ol",
        items: [
          "Structured Q&A you authored directly (highest authority).",
          "Uploaded documents (assumed to be the most current internal reference).",
          "Website content (public, easiest to update).",
        ],
      },
      {
        type: "callout",
        kind: "info",
        text: "Use structured Q&A as your override lever. When a customer keeps getting a subtly wrong answer, a single Q&A pair fixes it faster than editing pages.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Prefer HTML over PDF where possible — HTML is easier to update and the crawler parses it more reliably.",
          "Keep one canonical page per topic; delete duplicates.",
          "Write one Q&A per hard question your team gets asked repeatedly.",
          "Re-upload documents rather than adding new versions — the old file is replaced.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I connect a database directly?" },
      {
        type: "p",
        text: "Not directly. Export the data to CSV or expose it as an HTML page and connect that. Direct database connectors are on the Enterprise roadmap.",
      },
      { type: "h3", text: "How large a document library can I upload?" },
      {
        type: "p",
        text: "Pro workspaces have uploaded thousands of documents totalling several GB without issue. Per-file cap is 25 MB.",
      },
      { type: "h3", text: "Are my sources used to train shared models?" },
      {
        type: "p",
        text: "No. Your sources are private to your workspace and never leave it for model training.",
      },
    ],
  },
  {
    slug: "upload-documents",
    category: "Knowledge & training",
    title: "Upload PDFs, DOCX, Markdown and CSV to your AI Twin",
    summary:
      "Fill the gaps your website leaves. Add product docs, policies, spec sheets and internal knowledge — parsed, chunked and indexed automatically.",
    updated: "2026-06-19",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Documents are how you teach your AI Twin the things your public website deliberately leaves out — detailed pricing, specification sheets, internal SOPs, refund policies, warranty terms, product comparisons. Reality Twin parses, chunks and embeds every uploaded document within seconds so it's ready for retrieval.",
      },

      { type: "h2", text: "How to upload" },
      {
        type: "ol",
        items: [
          "Open Knowledge → Sources → Upload documents.",
          "Drag files onto the drop zone or click to select. Multi-file uploads are supported.",
          "Wait for each file's status to turn Indexed (typically 5–30 seconds per file).",
          "Ask your twin a question the document answers to confirm it retrieves correctly.",
        ],
      },

      { type: "h2", text: "Supported formats" },
      {
        type: "ul",
        items: [
          "PDF — text-based PDFs are parsed natively; scanned images without OCR are skipped.",
          "DOCX — Microsoft Word 2007+.",
          "Markdown (.md) — best format for structured internal docs.",
          "Plain text (.txt).",
          "CSV — parsed as a table with headers; each row becomes a retrievable fact.",
        ],
      },
      {
        type: "callout",
        kind: "info",
        text: "Maximum file size: 25 MB. For larger files, split into logical chunks (e.g. one PDF per product line) — the twin actually performs better with well-scoped documents.",
      },

      { type: "h2", text: "Keep your document library clean" },
      {
        type: "ul",
        items: [
          "Prefer well-structured documents with clear headings and short paragraphs.",
          "Remove draft and stale versions — the twin cannot tell them apart from current ones.",
          "Re-upload the file with the same name to update; the old version is replaced, not merged.",
          "Tag documents with categories in Knowledge → Sources for easier browsing.",
        ],
      },

      { type: "h2", text: "Working with PDFs specifically" },
      { type: "h3", text: "Text-based vs. scanned" },
      {
        type: "p",
        text: "PDFs generated from Word, InDesign, LaTeX, or web-to-PDF tools are text-based and parse perfectly. Scanned PDFs (an image of a document) do not — run them through OCR first (Preview on macOS, Adobe Acrobat, ocrmypdf on Linux).",
      },
      { type: "h3", text: "Multi-column layouts" },
      {
        type: "p",
        text: "The parser handles two-column layouts, tables and footnotes. Very complex layouts (multi-column with sidebars and floating boxes) may occasionally interleave text — export from the source as single-column if you notice retrieval quality drop.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Rename files clearly (pricing-2026-q3.pdf beats final-final-v7.pdf).",
          "One topic per file makes citations clearer to visitors.",
          "For price lists that change frequently, publish them as an HTML page instead and connect via the crawler.",
          "For legally sensitive documents (terms, DPA, privacy), use structured Q&A so you control the exact wording of every answer.",
        ],
      },

      { type: "h2", text: "Common mistakes" },
      {
        type: "ul",
        items: [
          "Uploading a PDF and forgetting to remove the older version — the twin may cite both.",
          "Uploading a 200 MB brand book — reduce to just the sections the twin needs.",
          "Assuming the twin reads images inside PDFs. It reads text only; caption important diagrams in the surrounding text.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "How many documents can I upload?" },
      {
        type: "p",
        text: "Starter — 10. Pro — unlimited. Enterprise — unlimited with dedicated ingestion pipeline for very large libraries.",
      },
      { type: "h3", text: "How long does indexing take?" },
      {
        type: "p",
        text: "Under 30 seconds for a typical 10-page PDF. Large documents (200+ pages) may take a minute or two.",
      },
      { type: "h3", text: "Are my documents encrypted at rest?" },
      {
        type: "p",
        text: "Yes — AES-256 at rest, TLS 1.3 in transit. See Where is my data stored?",
      },
      { type: "h3", text: "Can I upload confidential documents?" },
      {
        type: "p",
        text: "Yes. Set the document visibility to private so it is used only for your workspace's conversations and never exposed in Twin-to-Twin exchanges.",
      },
    ],
  },
  {
    slug: "force-recrawl",
    category: "Knowledge & training",
    title: "Force a manual re-crawl of your website",
    summary:
      "Push new pages, updated pricing or fresh content into your AI Twin immediately — from the dashboard, the API, or a CMS webhook.",
    updated: "2026-06-21",
    readMinutes: 5,
    body: [
      {
        type: "p",
        text: "The scheduled crawl runs weekly, which is fine for most sites. When you publish a new landing page or update a pricing figure, trigger a manual re-crawl so your AI Twin picks up the change within minutes.",
      },

      { type: "h2", text: "From the dashboard" },
      {
        type: "ol",
        items: [
          "Open Knowledge → Sources.",
          "Find your website entry and click Re-crawl.",
          "The status changes to Crawling. Typical time: 30–90 seconds for small sites.",
          "When status returns to Indexed, the new content is retrievable.",
        ],
      },

      { type: "h2", text: "From the REST API" },
      {
        type: "p",
        text: "Automate re-crawls from your deploy pipeline or CMS. Get your API key under Settings → API.",
      },
      {
        type: "code",
        lang: "bash",
        text: `curl -X POST https://realitytwin.io/api/v1/sources/{sourceId}/recrawl \\
  -H "Authorization: Bearer $RT_API_KEY"`,
      },
      {
        type: "p",
        text: "Response includes a jobId you can poll for status via GET /api/v1/jobs/{jobId}.",
      },

      { type: "h2", text: "From a CMS webhook" },
      { type: "p", text: "Point your CMS's publish webhook at:" },
      {
        type: "code",
        lang: "text",
        text: `POST https://realitytwin.io/api/v1/sources/{sourceId}/recrawl
Header: X-RT-Webhook-Secret: your-shared-secret`,
      },
      {
        type: "p",
        text: "Configure the shared secret under Settings → API → Webhook secrets. WordPress, Contentful, Sanity, Prismic and Strapi all support outgoing webhooks natively.",
      },

      { type: "h2", text: "Incremental vs. full re-crawls" },
      {
        type: "ul",
        items: [
          "Incremental (default) — refetches only pages whose Last-Modified header or sitemap lastmod has changed. Fast and cheap.",
          "Full — refetches every page. Use when you've restructured URLs or changed sitewide copy.",
        ],
      },
      {
        type: "p",
        text: "The dashboard button runs an incremental crawl by default; hold Shift when clicking to run a full crawl.",
      },

      { type: "h2", text: "How long do changes take to reach conversations?" },
      {
        type: "p",
        text: "As soon as the crawl finishes, new chunks are available for retrieval — usually within 30–90 seconds after the button click for a small site. There is no additional embedding lag.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Automate re-crawls on publish to eliminate stale answers permanently.",
          "For pricing pages, re-crawl the moment the change goes live.",
          "For blog content, weekly is enough.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Is there a limit on manual re-crawls?" },
      {
        type: "p",
        text: "Unlimited on all paid plans. The free plan is limited to one manual re-crawl per day.",
      },
      { type: "h3", text: "Will a re-crawl duplicate my content?" },
      { type: "p", text: "No. Existing chunks are updated in place; deleted pages are removed." },
      { type: "h3", text: "Can I re-crawl just a single page?" },
      {
        type: "p",
        text: 'Yes via the API: POST /api/v1/sources/{sourceId}/pages with a JSON body { urls: ["https://..."] }.',
      },
    ],
  },
  {
    slug: "fix-wrong-answers",
    category: "Knowledge & training",
    title: "Fix wrong or outdated AI Twin answers",
    summary:
      "When your AI Twin says something wrong, the fix is almost always in the source. Diagnose fast and remediate cleanly.",
    updated: "2026-06-23",
    readMinutes: 7,
    body: [
      {
        type: "p",
        text: "Reality Twin answers are grounded in your knowledge graph, with clickable citations for every response. When you spot a wrong answer, resist the urge to hard-code a response — instead trace it to the source, fix the source, and every future user (and future twin-to-twin exchange) gets the right answer.",
      },

      { type: "h2", text: "Step 1 — Reproduce the bad answer" },
      {
        type: "ol",
        items: [
          "Open Conversations and find the transcript in question.",
          "Ask the same question to your twin from the test panel to confirm it still misfires.",
          "Note the exact phrasing — small wording changes can flip retrieval.",
        ],
      },

      { type: "h2", text: "Step 2 — Read the citation" },
      {
        type: "p",
        text: "Every reply shows which sources it used. Click the citation to jump to the exact chunk. Ask:",
      },
      {
        type: "ul",
        items: [
          "Is the chunk factually wrong?",
          "Is it outdated?",
          "Does it contradict another source?",
          "Is it correct but retrieved without enough context?",
        ],
      },

      { type: "h2", text: "Step 3 — Apply the right fix" },
      { type: "h3", text: "Wrong fact" },
      {
        type: "p",
        text: "Update the source page or document. If it's a page, re-crawl. If it's a document, re-upload with the same name so the old version is replaced.",
      },
      { type: "h3", text: "Outdated content" },
      {
        type: "p",
        text: "Publish the current version, then trigger a manual re-crawl — see Force a re-crawl of your website.",
      },
      { type: "h3", text: "Contradictory sources" },
      { type: "p", text: "Two sources disagree, and retrieval picked the wrong one. Options:" },
      {
        type: "ul",
        items: [
          "Delete the losing source.",
          "Update it to match the winning one.",
          "Add a structured Q&A that authoritatively answers the question — Q&A ranks highest.",
        ],
      },
      { type: "h3", text: "Missing context" },
      {
        type: "p",
        text: "The chunk is correct but retrieved in isolation. Add a summary paragraph at the top of the source, or add a Q&A that stitches the context together.",
      },

      { type: "h2", text: "Step 4 — Verify" },
      {
        type: "p",
        text: "Ask the original question again — plus 2–3 rephrasings. Retrieval should now return the corrected source.",
      },

      { type: "h2", text: "Never do this" },
      {
        type: "callout",
        kind: "warn",
        text: "Do not edit the twin's response text directly. It masks the bug in one conversation while leaving every other visitor exposed. Fix the knowledge, not the answer.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Review a random sample of conversations weekly — five is enough to spot systemic issues.",
          "Turn recurring misfires into structured Q&As so they never come back.",
          "Keep a changelog of source edits — future you will thank you.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Why does my AI Twin hallucinate?" },
      {
        type: "p",
        text: "It shouldn't. Reality Twin is retrieval-grounded — replies without a citation are refused or handed off. If you see a confident answer without citations, report it to hello@realitytwin.io.",
      },
      { type: "h3", text: "Can I mark answers as wrong from the transcript?" },
      {
        type: "p",
        text: "Yes. Every message in Conversations has a thumbs-down. Marked messages appear under Knowledge → Review with the citation, so you can fix them in one place.",
      },
      { type: "h3", text: "How quickly does a source fix take effect?" },
      {
        type: "p",
        text: "For document re-uploads: seconds. For re-crawls: 30–90 seconds. For structured Q&A: immediate.",
      },
    ],
  },

  // ── Personality & tone ──────────────────────────────────────────
  {
    slug: "voice-tone",
    category: "Personality & tone",
    title: "Set your AI Twin's voice, tone and verbosity",
    summary:
      "Make your AI Twin sound like your brand — formal or playful, terse or detailed, confident or careful — with granular sliders and a system prompt.",
    updated: "2026-06-17",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "A brand-aligned voice is the difference between an AI Twin that visitors trust and one that feels off. Reality Twin gives you four sliders and a free-form system prompt, so you can dial in exactly how your twin sounds.",
      },

      { type: "h2", text: "Where to configure" },
      {
        type: "p",
        text: "Open Twin → Personality. All changes are per-twin and go live within seconds — no re-crawl or re-index required.",
      },

      { type: "h2", text: "The four sliders" },
      {
        type: "ul",
        items: [
          "Voice — formal ↔ casual. Formal for legal, financial and enterprise B2B. Casual for consumer, creator, and DTC.",
          "Warmth — professional ↔ friendly. How much emotional register the twin brings to greetings, apologies and small talk.",
          "Verbosity — terse ↔ detailed. Terse gives one-sentence answers; detailed gives full context with examples.",
          "Confidence — hedge ↔ assertive. Hedging is safer for regulated topics; assertive is better for product recommendations.",
        ],
      },

      { type: "h2", text: "System prompt" },
      {
        type: "p",
        text: "Beneath the sliders is a system prompt field. Use it for brand-voice notes that don't fit the sliders:",
      },
      {
        type: "ul",
        items: [
          "Forbidden phrases (\"never use the word 'utilize'\").",
          'Formatting rules ("always use British English", "never use emoji", "sign off with — The Acme Team").',
          'Signature turns of phrase ("start every reply with a one-line summary").',
          "House metaphors and vocabulary that make your brand recognisable.",
        ],
      },
      {
        type: "callout",
        kind: "info",
        text: "Keep the system prompt under 500 words — longer prompts dilute the sliders and can produce inconsistent output.",
      },

      { type: "h2", text: "Language and localisation" },
      {
        type: "p",
        text: 'By default the twin replies in the language it detects from the visitor\'s message. To force a specific language, add a line to the system prompt ("Always reply in German, no matter what the visitor writes"). Reality Twin supports 40+ languages out of the box.',
      },

      { type: "h2", text: "Iterating on the voice" },
      {
        type: "p",
        text: "Change the sliders or the system prompt and the next conversation already uses them — there is no deploy step. Judge the effect with the numbers you already have: lead volume and quality in Analytics, and the knowledge-gap report for questions the twin fumbled. Change one thing at a time, give it a week of real traffic, then decide.",
      },

      { type: "h2", text: "Recipes to start from" },
      { type: "h3", text: "Consumer / DTC brand" },
      {
        type: "p",
        text: 'Voice: casual. Warmth: friendly. Verbosity: terse. Confidence: assertive. System prompt: "You are the voice of a fun, opinionated brand. Use British English. No emoji. Sign off with — The [Brand] Crew."',
      },
      { type: "h3", text: "B2B SaaS" },
      {
        type: "p",
        text: 'Voice: neutral. Warmth: professional. Verbosity: balanced. Confidence: assertive. System prompt: "You are a helpful product expert. Always answer with a one-line summary followed by 2–3 bullet details. Cite sources."',
      },
      { type: "h3", text: "Regulated (finance, legal, health)" },
      {
        type: "p",
        text: 'Voice: formal. Warmth: professional. Verbosity: detailed. Confidence: hedged. System prompt: "You are not a licensed professional. Never give personalised advice. Always encourage the visitor to consult a qualified [profession] before acting."',
      },

      { type: "h2", text: "Common mistakes" },
      {
        type: "ul",
        items: [
          "Setting every slider to the extreme — the twin becomes a caricature.",
          "Contradicting the sliders in the system prompt.",
          "Copying a competitor's voice — it never fits your actual brand.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can each twin have a different personality?" },
      {
        type: "p",
        text: "Yes. Personality is per-twin — run a formal twin for enterprise buyers and a casual twin for consumer traffic in the same workspace.",
      },
      { type: "h3", text: "Does personality affect what the twin knows?" },
      {
        type: "p",
        text: "No. Knowledge is separate. Personality only changes how answers are phrased.",
      },
      { type: "h3", text: "How do I make the twin shorter?" },
      {
        type: "p",
        text: 'Slide Verbosity toward terse and add "Answer in 1–2 sentences unless asked for detail" to the system prompt.',
      },
    ],
  },
  {
    slug: "topic-boundaries",
    category: "Personality & tone",
    title: "Set topic boundaries and refusals for your AI Twin",
    summary:
      "Tell your AI Twin what to avoid — competitor comparisons, legal advice, roadmap speculation — and how to decline gracefully.",
    updated: "2026-06-16",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Topic boundaries are hard rules your AI Twin will not cross. When a visitor asks about a forbidden topic, the twin refuses politely, explains why, and offers a graceful next step — usually a redirect to sales or a human handoff. Boundaries are essential for regulated industries and for keeping your twin off the record on sensitive commercial topics.",
      },

      { type: "h2", text: "How boundaries differ from the system prompt" },
      {
        type: "p",
        text: "The system prompt shapes voice and formatting. Boundaries are enforced with a separate retrieval-time and generation-time check — they cannot be jailbroken by a cleverly worded visitor question, and they log every refusal for audit.",
      },

      { type: "h2", text: "Add a boundary rule" },
      {
        type: "ol",
        items: [
          "Open Twin → Personality → Boundaries.",
          'Click Add rule and describe the topic in plain English ("Do not compare us to competitor X").',
          "Optionally list example phrasings that should trip the rule.",
          "Pick the refusal style: polite decline, redirect to sales, route to human, or custom response text.",
          "Save. The rule is active within seconds.",
        ],
      },

      { type: "h2", text: "Common boundary patterns" },
      {
        type: "ul",
        items: [
          "Legal, medical or financial advice — decline with a disclaimer and encourage a qualified professional.",
          "Pricing quotes above a threshold — redirect to sales for a tailored quote.",
          "Roadmap questions — offer a waitlist signup instead of a date.",
          "Competitor comparisons — decline politely and redirect to your own comparison pages.",
          "Confidential customer stories — refuse and offer public case studies instead.",
          "Employment / hiring questions — redirect to /careers.",
        ],
      },

      { type: "h2", text: "Regulated industry starter kit" },
      {
        type: "p",
        text: "For finance, health, legal and regulated commerce, we recommend these baseline rules:",
      },
      {
        type: "ul",
        items: [
          "No personalised advice — always general information plus a call to consult a professional.",
          "No forward-looking statements about performance or outcomes.",
          "No PII collection beyond email and company name.",
          "Automatic human handoff for any explicit request for a decision.",
        ],
      },

      { type: "h2", text: "Refusal styles" },
      { type: "h3", text: "Polite decline" },
      {
        type: "p",
        text: "A short, friendly explanation. Good for questions that are simply off-topic.",
      },
      { type: "h3", text: "Redirect to sales" },
      {
        type: "p",
        text: "Refuses to answer directly and asks for the visitor's work email to route to sales.",
      },
      { type: "h3", text: "Route to human" },
      {
        type: "p",
        text: "Immediately opens the human handoff flow — see Configure human handoff.",
      },
      { type: "h3", text: "Custom response" },
      {
        type: "p",
        text: "Write the exact refusal text yourself. Useful for legally reviewed language.",
      },

      { type: "h2", text: "Audit and review" },
      {
        type: "p",
        text: "Every boundary refusal is logged under Conversations with a Refusal tag. Filter by tag to review — you'll spot both real bad-actor probes and legitimate questions your rules are being too aggressive about.",
      },

      { type: "h2", text: "Common mistakes" },
      {
        type: "ul",
        items: [
          "Writing overly broad rules that catch legitimate questions.",
          "Forgetting to test the rule — always ask the twin a boundary-adjacent question after saving.",
          "Relying on the system prompt for compliance — use boundaries instead, which are enforced separately.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can visitors override a boundary by rephrasing?" },
      {
        type: "p",
        text: "No. Boundaries are checked at both retrieval and generation, so rewording the question does not bypass them.",
      },
      { type: "h3", text: "How many boundary rules can I add?" },
      {
        type: "p",
        text: "Unlimited on all paid plans. Practically, most workspaces run 5–15 rules.",
      },
      { type: "h3", text: "Do boundaries apply to Twin-to-Twin exchanges?" },
      {
        type: "p",
        text: "Yes — see Control what your twin discloses. Boundaries are enforced in every context.",
      },
    ],
  },
  {
    slug: "human-handoff",
    category: "Personality & tone",
    title: "Configure human handoff from AI Twin to your team",
    summary:
      "Decide when a real person should take over, how the AI Twin summarises the conversation, and where the handoff notification lands.",
    updated: "2026-06-15",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Even the best AI Twin should know when to step aside. Human handoff makes that transition seamless: the twin summarises the conversation, notifies the right person, and the widget flips into live-chat mode so a human can pick up where the twin left off — then hands back to the twin for follow-up questions.",
      },

      { type: "h2", text: "Handoff triggers" },
      {
        type: "ul",
        items: [
          'Visitor explicitly asks for a human ("can I talk to someone?", "is there a human?").',
          "Lead score crosses a threshold you set (default 70/100).",
          "Detected frustration via sentiment analysis (available on Pro).",
          "A boundary rule set to Route to human is triggered.",
          "Business hours (only route to human when someone is on shift).",
        ],
      },

      { type: "h2", text: "Where the handoff notification lands" },
      {
        type: "ul",
        items: [
          "Email — every plan.",
          "Slack — connect under Settings → Integrations.",
          "Microsoft Teams — connect under Settings → Integrations (Pro and above).",
          "SMS / mobile push — on Pro via Twilio integration.",
          "Webhook — POST to any endpoint you choose.",
        ],
      },

      { type: "h2", text: "How a handoff feels to the visitor" },
      {
        type: "ol",
        items: [
          'The twin says something like "I\'ll bring in a teammate — one moment."',
          'The widget shows a subtle status indicator ("A human is joining").',
          "When the teammate replies from the dashboard, the message appears with their name and avatar.",
          "When the human closes the conversation, the twin resumes and can answer follow-ups.",
        ],
      },

      { type: "h2", text: "The handoff brief" },
      { type: "p", text: "The notification includes a short brief written by the twin:" },
      {
        type: "ul",
        items: [
          "Who the visitor is (email if captured, company from enrichment).",
          "What they came for.",
          "What they've already been told.",
          "Why the twin is handing off.",
          "A deep link to the live conversation.",
        ],
      },
      {
        type: "callout",
        kind: "info",
        text: "The brief is designed to be read in under 15 seconds so your teammate can respond fast.",
      },

      { type: "h2", text: "Business hours and coverage" },
      {
        type: "p",
        text: "Configure your team's working hours under Settings → Handoff. Outside hours, the twin either continues autonomously or offers a callback form — your choice.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Route high-intent leads only — over-routing burns out your team.",
          "Set a callback form for out-of-hours to catch leads without waking anyone.",
          "Review handoff transcripts weekly to spot conversations the twin could have handled itself.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can multiple teammates be notified at once?" },
      {
        type: "p",
        text: "Yes. Configure a distribution list or a Slack channel that alerts your whole team; the first responder claims the conversation.",
      },
      { type: "h3", text: "What if no one responds?" },
      {
        type: "p",
        text: "After a configurable delay (default 5 minutes) the twin apologises, offers a callback form and continues autonomously.",
      },
      { type: "h3", text: "Can the twin resume after a human closes the conversation?" },
      {
        type: "p",
        text: "Yes. The next message from the visitor is handled by the twin again unless it triggers another handoff.",
      },
    ],
  },

  // ── Leads & conversations ──────────────────────────────────────
  {
    slug: "conversation-history",
    category: "Leads & conversations",
    title: "Read, search and export AI Twin conversation history",
    summary:
      "Every AI Twin transcript is stored, searchable, filterable and exportable — the raw material for improving your twin and closing leads.",
    updated: "2026-06-14",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Your AI Twin's conversation history is a goldmine: it tells you what visitors actually want, which questions your website doesn't answer, and which leads are worth calling. Reality Twin stores every transcript with full context and gives you fast search, granular filters, and clean exports.",
      },

      { type: "h2", text: "Browse conversations" },
      {
        type: "ul",
        items: [
          "Open Conversations. The list is sorted newest first.",
          "Filter by date range, lead score, source page, country, language or handoff status.",
          "Full-text search across every message body.",
          "Click any conversation to see the full thread with citations and enrichment data.",
        ],
      },

      { type: "h2", text: "What a transcript contains" },
      {
        type: "ul",
        items: [
          "Visitor session ID and (if captured) email, name, company.",
          "Enrichment: industry, headcount, region (from the work email domain).",
          "Every message with timestamp and, for twin replies, the citations used.",
          "Lead score with the signals that produced it.",
          "Handoff events and human replies.",
        ],
      },

      { type: "h2", text: "Export" },
      {
        type: "p",
        text: "From the top-right menu, export the current filter as CSV or JSON. The export includes visitor metadata, the full message-by-message transcript, citations, lead score and handoff events. CSV is great for pasting into a spreadsheet; JSON is easier to feed into your own analytics.",
      },
      {
        type: "code",
        lang: "bash",
        text: `# API export example
curl "https://realitytwin.io/api/v1/conversations?from=2026-06-01&format=json" \\
  -H "Authorization: Bearer $RT_API_KEY" > conversations.json`,
      },

      { type: "h2", text: "Retention" },
      {
        type: "p",
        text: "Default retention is 24 months. Change it per workspace in Settings → Data → Retention. When conversations are deleted, they're purged from primary storage immediately and from backups within 90 days.",
      },

      { type: "h2", text: "Data subject requests" },
      {
        type: "p",
        text: "To satisfy a GDPR / FADP right-to-erasure request, search by email under Settings → Data → Personal data. Export or delete all data tied to that person in one click.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Review conversations weekly — 20 minutes a week catches most knowledge gaps.",
          "Tag high-intent conversations to track outcomes into your CRM.",
          "Export monthly for offline analysis or investor reporting.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I share a single transcript with a colleague?" },
      {
        type: "p",
        text: "Yes. Each conversation has a Share link that grants read-only access to any workspace teammate.",
      },
      { type: "h3", text: "Are transcripts used to train shared models?" },
      {
        type: "p",
        text: "No. Transcripts stay in your workspace and are never used for cross-customer training.",
      },
      { type: "h3", text: "Can I delete a single conversation?" },
      {
        type: "p",
        text: "Yes — from the conversation detail view, or via DELETE /api/v1/conversations/{id}.",
      },
    ],
  },
  {
    slug: "lead-scoring",
    category: "Leads & conversations",
    title: "How AI Twin lead scoring works",
    summary:
      "The signals Reality Twin uses to score every conversation for intent and fit — and how to tune the notification threshold.",
    updated: "2026-06-13",
    readMinutes: 7,
    body: [
      {
        type: "p",
        text: "Every AI Twin conversation is scored 0–100 on two axes: intent (does this visitor want to buy?) and fit (are they the right customer for you?). The combined score determines whether the conversation reaches your inbox in real time, gets pushed to your CRM, or stays quietly logged for later review.",
      },

      { type: "h2", text: "Intent signals" },
      {
        type: "ul",
        items: [
          'Explicit buying language — "how much", "can we start", "send a quote", "trial".',
          "Question specificity — asking about your integrations vs. asking what you do.",
          "Depth of the conversation — more turns typically means higher intent.",
          "Time on page before starting the conversation.",
          "Repeat visits within the same session.",
          "Filling out contact fields voluntarily.",
        ],
      },

      { type: "h2", text: "Fit signals" },
      {
        type: "ul",
        items: [
          "Work email domain — matches your ideal customer profile (ICP) industries and headcount bands.",
          "Firmographic enrichment — industry, headcount, region, tech stack (best-effort from public data).",
          "Referrer — visitors from a target account list rank higher.",
          "Language and geography — match against your served regions.",
        ],
      },

      { type: "h2", text: "Define your ideal customer profile (ICP)" },
      { type: "p", text: "Settings → Leads → ICP. Describe the customer you want to prioritise:" },
      {
        type: "ul",
        items: [
          "Industries (SaaS, e-commerce, agencies, etc.).",
          "Headcount bands (1–10, 11–50, 51–200, 200+).",
          "Regions.",
          "Optional target account list (upload CSV of company domains).",
        ],
      },
      {
        type: "p",
        text: "Conversations matching your ICP get a fit boost; those clearly outside get down-weighted.",
      },

      { type: "h2", text: "Notification threshold" },
      {
        type: "p",
        text: "By default, conversations scoring above 70 land in your inbox in real time. Adjust in Settings → Leads → Notifications. Common patterns:",
      },
      {
        type: "ul",
        items: [
          "Threshold 80 — noisy sites where only very-high intent should page a human.",
          "Threshold 50 — early-stage products where any warm lead is precious.",
          "Threshold 90 with CRM sync at 70 — the CRM captures more, sales gets fewer pings.",
        ],
      },

      { type: "h2", text: "How the score is presented" },
      {
        type: "p",
        text: "Every conversation shows the score with a short explanation — which signals fired, and by how much. This is the same explanation that goes into the CRM sync so your salespeople know why a lead landed on their desk.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Tune the threshold weekly for the first month — you'll converge fast.",
          "Feed closed-won and closed-lost outcomes back into Reality Twin (via CRM sync) to improve scoring over time.",
          "Use a lower threshold for CRM sync than for real-time notifications.",
        ],
      },

      { type: "h2", text: "Common mistakes" },
      {
        type: "ul",
        items: [
          "Setting the threshold too low and ignoring notifications — nothing gets acted on.",
          "Not defining an ICP — every conversation ends up mid-scored.",
          "Trusting fit signals blindly — always read the transcript before calling.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I write my own scoring rules?" },
      {
        type: "p",
        text: "Yes on Pro. Rule Builder lets you add + or − points on any signal or keyword match.",
      },
      { type: "h3", text: "Does lead scoring work in every language?" },
      { type: "p", text: "Yes. Intent detection is multilingual across 40+ languages." },
      { type: "h3", text: "Where does enrichment data come from?" },
      {
        type: "p",
        text: "Public sources indexed by our partners — WHOIS, company websites, and publicly available firmographic data. No paid B2B database is required.",
      },
    ],
  },
  {
    slug: "crm-sync",
    category: "Leads & conversations",
    title: "Send AI Twin leads to your CRM (HubSpot, Salesforce, any)",
    summary:
      "Push every qualified lead into your CRM the moment it happens, using the signed lead webhook — directly or through Zapier, Make or n8n.",
    updated: "2026-08-03",
    readMinutes: 7,
    body: [
      {
        type: "p",
        text: "Every lead your twin captures can be pushed into your CRM the moment it happens. Today this runs through a signed webhook, which works with any CRM — including HubSpot, Salesforce, Pipedrive, Attio or an internal system — either directly or through an automation tool.",
      },
      {
        type: "callout",
        kind: "info",
        text: "Native one-click apps for HubSpot and Salesforce are on the roadmap and not available yet. What's described below ships today. You can always check the live status of any capability at realitytwin.io/roadmap.",
      },

      { type: "h2", text: "Set up the webhook" },
      {
        type: "ol",
        items: [
          "Settings → Integrations → Webhook.",
          "Paste the URL that should receive leads (your endpoint, or a Zapier/Make/n8n catch hook).",
          "Save. Reality Twin generates a signing secret — copy it.",
          "Use Send test to fire a sample payload and confirm your endpoint answers with 2xx.",
        ],
      },

      { type: "h2", text: "What we send" },
      {
        type: "p",
        text: "Reality Twin POSTs JSON for each new lead. This is the actual payload — no enrichment, no invented fields:",
      },
      {
        type: "code",
        lang: "json",
        text: `{
  "name": "Jane Visitor",
  "email": "jane@acme.com",
  "question": "Do you deliver to Austria?",
  "intent_score": 74,
  "lead_score": 94,
  "status": "hot",
  "source": "twin_chat"
}`,
      },
      {
        type: "ul",
        items: [
          "name / email — what the visitor actually gave you.",
          "question — the message that triggered the capture.",
          "intent_score and lead_score — 0–100, how strong the buying signal was.",
          "status — hot or warm, derived from intent.",
          "source — twin_chat, contact_form, and so on.",
        ],
      },

      { type: "h2", text: "Verify the signature" },
      {
        type: "p",
        text: "Every request carries an HMAC signature generated with your secret, so your endpoint can prove the call really came from us. Reject anything that doesn't match — otherwise anyone who learns your URL can inject leads.",
      },

      { type: "h2", text: "Reaching HubSpot or Salesforce today" },
      {
        type: "p",
        text: 'Point the webhook at a Zapier, Make or n8n catch hook and map the fields onto "Create/Update Contact" in your CRM. Ten minutes of setup, no code. If you have engineering capacity, posting to the CRM\'s own API from a small handler gives you full control over field mapping and deduplication.',
      },
      {
        type: "p",
        text: "Deduplication is handled by your CRM, not by us — both HubSpot and Salesforce match on email out of the box.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Auto-assign Reality Twin leads to a specific queue or owner so they don't sit unclaimed.",
          "Store lead_score as a numeric field — it's what makes the source reportable.",
          "Tag the source so you can prove where the pipeline came from.",
          "Answer fast: leads contacted within the hour convert best.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I send low-score leads to CRM too?" },
      {
        type: "p",
        text: "Yes — set the threshold to 0. Most teams don't, because it clutters the pipeline.",
      },
      { type: "h3", text: "Is the sync one-way or two-way?" },
      {
        type: "p",
        text: "One-way: Reality Twin → your CRM. Nothing is read back out of your CRM.",
      },
      { type: "h3", text: "What if my endpoint is down?" },
      {
        type: "p",
        text: "The lead is still captured in Reality Twin — you'll find it under Leads and in the CRM view, so nothing is lost while you fix the endpoint.",
      },
    ],
  },

  // ── Network & marketplace ──────────────────────────────────────
  {
    slug: "marketplace-profile",
    category: "Network & marketplace",
    title: "Publish your AI Twin marketplace profile",
    summary:
      "Make your AI Twin discoverable to other twins and to Google — with a public, SEO-indexed profile you fully control.",
    updated: "2026-06-11",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Your marketplace profile is a public page at realitytwin.io/marketplace/YOUR-SLUG — indexable by Google, browsable by humans, and discoverable by other twins in the Reality Twin network. Publishing your profile is how you get inbound Twin-to-Twin introductions without cold outreach.",
      },

      { type: "h2", text: "Set up your profile" },
      {
        type: "ol",
        items: [
          "Open Twin → Marketplace.",
          "Toggle Publish profile on.",
          "Fill in your public description, what you offer, and what you're looking for.",
          "Optionally add case studies, logos, and a link to book a call.",
          "Save. Your public URL is live within seconds and submitted to Google via the sitemap.",
        ],
      },

      { type: "h2", text: "What appears on the public profile" },
      {
        type: "ul",
        items: [
          "Your company name, logo and one-line description.",
          "What you offer — the products or services.",
          "Who you serve — target industries, regions and headcount bands.",
          "What you're looking for — partners, customers, integrations.",
          "Public case studies you add manually.",
          "A public chat surface where visitors can talk to your twin.",
        ],
      },

      { type: "h2", text: "What stays private" },
      {
        type: "ul",
        items: [
          "Uploaded documents.",
          "Conversation transcripts.",
          "Lead pipeline and CRM data.",
          "Team members and billing.",
        ],
      },

      { type: "h2", text: "SEO signals we set for you" },
      {
        type: "ul",
        items: [
          "Unique title and meta description per profile.",
          "Schema.org Organization + Service structured data.",
          "OpenGraph and Twitter Card metadata.",
          "Canonical URL back to your profile.",
          "Inclusion in the Reality Twin marketplace sitemap submitted to Google.",
        ],
      },

      { type: "h2", text: "Publish and withdraw" },
      {
        type: "p",
        text: "Toggle the profile off any time. The public URL returns 410 Gone within minutes and drops from Google on the next crawl. Uploaded logos and case studies remain in your workspace so re-publishing is one click.",
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Write for a human first — the twin-to-twin protocol reads the same copy.",
          "Add specific numbers (industries served, headcount bands, geographies).",
          "Refresh case studies quarterly — Google rewards updated content.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Is publishing free?" },
      { type: "p", text: "Yes. All paid plans include marketplace publishing." },
      { type: "h3", text: "Can competitors see my profile?" },
      {
        type: "p",
        text: "Anything on the public profile is public. Anything you mark private stays private, and Twin-to-Twin exchanges only expose what you have declared shareable.",
      },
      { type: "h3", text: "Does my profile appear in Google?" },
      {
        type: "p",
        text: "Yes — usually within days of publishing. The public profile has clean SEO metadata and is included in our sitemap.",
      },
    ],
  },
  {
    slug: "twin-to-twin-protocol",
    category: "Network & marketplace",
    title: "The Twin-to-Twin protocol explained",
    summary:
      "How two AI Twins in the Reality Twin network find each other, propose an introduction, and broker a warm human-to-human intro — with consent on both sides.",
    updated: "2026-06-10",
    readMinutes: 8,
    body: [
      {
        type: "p",
        text: "The Twin-to-Twin (T2T) protocol replaces cold outreach with automated, consent-based discovery between AI Twins that have already been briefed on both sides. When two twins agree there is a fit, they propose the intro to their humans — nobody sends a cold email, and no calendar is opened until both sides have accepted.",
      },

      { type: "h2", text: "Why T2T beats cold outreach" },
      {
        type: "ul",
        items: [
          "No cold email. The intro is warm because both twins vetted the fit.",
          "No wasted meetings. Only accepted intros ever hit calendars.",
          "Consent by design. Nothing is disclosed to a human without approval.",
          "Symmetric. Both sides gain — no asymmetric outreach exhaustion.",
        ],
      },

      { type: "h2", text: "The flow, step by step" },
      {
        type: "ol",
        items: [
          "Each twin publishes a public capability & intent profile (see Publish your marketplace profile).",
          "Reality Twin continuously matches profiles across the network based on capability, intent and fit signals.",
          "When two twins align, they open a private twin-to-twin session — never visible to humans.",
          "The twins negotiate: is there a genuine fit? What would each side want from the intro?",
          "If both twins agree, each proposes the intro to its own team inside the dashboard.",
          "If both humans accept, a warm intro email fires — human-to-human — with a short brief written by the twins.",
        ],
      },

      { type: "h2", text: "Consent gates" },
      { type: "p", text: "Three explicit gates prevent surprise disclosures:" },
      {
        type: "ul",
        items: [
          "Gate 1 — your source visibility settings decide what is even up for T2T disclosure.",
          "Gate 2 — the twin can only propose intros aligned with your published intent.",
          "Gate 3 — a human on your side must accept the intro before your identity or contact is shared with the other party.",
        ],
      },

      { type: "h2", text: "What twins can and cannot exchange" },
      {
        type: "ul",
        items: [
          "Can — public profile data, published intent, high-level fit signals.",
          "Can — a proposed intro brief (drafted, previewed and edited by you before sending).",
          "Cannot — private documents, private transcripts, customer PII, pricing tables (unless you make them public).",
        ],
      },

      { type: "h2", text: "Rate limits and abuse controls" },
      {
        type: "ul",
        items: [
          "Each twin can send at most a small daily number of intro proposals — spam is impossible by design.",
          "A twin that receives repeated declines is throttled automatically.",
          "Every T2T session is logged and auditable in your workspace.",
        ],
      },

      { type: "h2", text: "How to get more T2T intros" },
      {
        type: "ul",
        items: [
          "Publish and complete your marketplace profile.",
          'Write specific intent ("We\'re looking for European ecommerce brands doing €5M+ ARR") — vague intent produces vague matches.',
          "Add fresh case studies quarterly.",
          "Keep your knowledge graph up to date so the twin can answer T2T questions accurately.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I opt out of T2T entirely?" },
      {
        type: "p",
        text: "Yes. Twin → Marketplace → toggle Twin-to-Twin off. Your marketplace profile can stay public without participating in T2T.",
      },
      { type: "h3", text: "Do I pay per intro?" },
      {
        type: "p",
        text: "No. T2T is included in Pro and Enterprise. Free and Starter can receive intros but not send.",
      },
      { type: "h3", text: "What if I decline an intro?" },
      {
        type: "p",
        text: "The other side is told only that the timing wasn't right — no reason, no identity beyond what was already public.",
      },
    ],
  },
  {
    slug: "twin-disclosure",
    category: "Network & marketplace",
    title: "Control what your AI Twin discloses in Twin-to-Twin exchanges",
    summary:
      "Three layers of control over what your AI Twin will and won't reveal to other twins — source visibility, field-level redaction and category boundaries.",
    updated: "2026-06-09",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Reality Twin gives you three independent layers of control over what your AI Twin discloses to other twins in the network. Layer them together to get the exact disclosure posture your legal, sales and product teams are comfortable with.",
      },

      { type: "h2", text: "Layer 1 — Source visibility" },
      {
        type: "p",
        text: "The coarsest control. For every source (website, document, Q&A) mark it public (usable in T2T) or private (never leaves your workspace). Private by default for uploaded documents; public by default for the website.",
      },
      {
        type: "callout",
        kind: "info",
        text: "Rule of thumb: if it lives on your public website, it is fine for T2T. If it lives only in a shared drive, keep it private unless you explicitly opt in.",
      },

      { type: "h2", text: "Layer 2 — Field-level redaction" },
      {
        type: "p",
        text: "Add regex or literal rules to redact specific values inside otherwise-public sources. The twin substitutes [redacted] and refuses to expand.",
      },
      {
        type: "code",
        lang: "text",
        text: `# Redact any 4-digit customer count
Pattern: /\\b\\d{4}\\s+customers\\b/

# Never mention a specific competitor's name
Literal: "AcmeCompetitor"

# Redact all internal project codenames
Pattern: /Project [A-Z][a-z]+/`,
      },

      { type: "h2", text: "Layer 3 — Category boundaries" },
      {
        type: "p",
        text: "The same topic-boundary system you use for customer conversations, applied to twin-to-twin exchanges. Common T2T-specific boundaries:",
      },
      {
        type: "ul",
        items: [
          "Never disclose current pricing to competitor twins.",
          "Never confirm or deny customer names.",
          "Never speculate about the roadmap.",
        ],
      },

      { type: "h2", text: "Auditing what was disclosed" },
      {
        type: "p",
        text: "Every T2T exchange is logged under Network → T2T sessions with the exact messages exchanged. Filter to see:",
      },
      {
        type: "ul",
        items: [
          "What information was shared in each session.",
          "Which boundary rules fired.",
          "Which sessions produced intros and which did not.",
        ],
      },

      { type: "h2", text: "Best practices" },
      {
        type: "ul",
        items: [
          "Start conservative — mark everything private, then open specific sources.",
          "Have legal review your public sources annually.",
          "Add a boundary for anything you would not put in an outbound email.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I have different disclosure rules for different partner types?" },
      {
        type: "p",
        text: "Yes on Enterprise — segmented disclosure lets you disclose more to prospects than to competitors.",
      },
      { type: "h3", text: "What happens if a boundary fires mid-exchange?" },
      {
        type: "p",
        text: "The twin declines gracefully and the session continues, logged for your review.",
      },
      { type: "h3", text: "Is any disclosure automatic without my say-so?" },
      {
        type: "p",
        text: "Only what you have published on your marketplace profile is automatic. Everything else requires the layers above to allow it.",
      },
    ],
  },

  // ── Billing & plans ─────────────────────────────────────────────
  {
    slug: "compare-plans",
    category: "Billing & plans",
    title: "Compare Free, Starter, Pro and Enterprise",
    summary:
      "Full feature and limit comparison across every Reality Twin plan — and how to pick the right one for your stage.",
    updated: "2026-06-08",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Reality Twin has four plans: Free, Starter, Pro and Enterprise. This article covers what's included in each, the differences that matter, and how to pick — plus the full pricing detail on the /pricing page.",
      },

      { type: "h2", text: "Free" },
      { type: "p", text: "For evaluating the product on a real site." },
      {
        type: "ul",
        items: [
          "100 conversations / month.",
          "1 AI Twin.",
          "Website crawl up to 100 pages.",
          "Structured Q&A.",
          "Email support.",
          '"Powered by Reality Twin" badge in the widget.',
        ],
      },

      { type: "h2", text: "Starter" },
      { type: "p", text: "For a single founder or small team going live." },
      {
        type: "ul",
        items: [
          "2,500 conversations / month.",
          "Up to 3 AI Twins.",
          "Website crawl up to 1,000 pages, 10 uploaded documents.",
          "HubSpot / Salesforce CRM sync.",
          "Slack notifications.",
          "Marketplace publishing.",
          "Remove the badge.",
          "5 team seats included.",
        ],
      },

      { type: "h2", text: "Pro" },
      { type: "p", text: "For growing teams that want the full platform." },
      {
        type: "ul",
        items: [
          "25,000 conversations / month.",
          "Unlimited AI Twins.",
          "Unlimited knowledge sources.",
          "Twin-to-Twin protocol (send intros).",
          "Opportunity engine and Analytics Pro.",
          "Knowledge-gap reporting and freshness scoring.",
          "Appointment booking and paid service requests in chat.",
          "Lead scoring with intent signals.",
          "Priority support (target reply within 4 business hours).",
          "20 team seats included.",
        ],
      },

      { type: "h2", text: "Enterprise" },
      { type: "p", text: "For regulated industries and large organisations." },
      {
        type: "ul",
        items: [
          "Custom conversation volume.",
          "Dedicated region, including Switzerland.",
          "SSO / SAML, SCIM provisioning.",
          "Private model deployment.",
          "DPA + SLA (99.9% uptime target).",
          "Named customer success manager.",
          "Roadmap early access.",
          "Unlimited seats.",
        ],
      },

      { type: "h2", text: "How to choose" },
      {
        type: "ul",
        items: [
          "Just testing — Free.",
          "Live on one site, want leads in your inbox — Starter.",
          "Multiple brands, sales team, network-driven growth — Pro.",
          "Regulated, custom region, procurement / security review — Enterprise.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I switch plans mid-month?" },
      {
        type: "p",
        text: "Yes — see Change or cancel your plan. Upgrades are immediate and prorated; downgrades apply at period end.",
      },
      { type: "h3", text: "Is there an annual discount?" },
      { type: "p", text: "Yes — two months free on Starter and Pro when billed annually." },
      { type: "h3", text: "Do you offer discounts for non-profits or education?" },
      { type: "p", text: "Yes — email hello@realitytwin.io with proof of status." },
    ],
  },
  {
    slug: "conversation-definition",
    category: "Billing & plans",
    title: "What counts as a conversation on Reality Twin?",
    summary:
      "Exactly how AI Twin usage is metered — session-based, bot-filtered, and predictable. No surprises on your invoice.",
    updated: "2026-06-07",
    readMinutes: 5,
    body: [
      {
        type: "p",
        text: "Reality Twin meters usage by conversation, not by message. This article explains exactly what counts, what doesn't, and how overages work — so you can size a plan confidently and never be surprised by a bill.",
      },

      { type: "h2", text: "The definition" },
      {
        type: "p",
        text: "A conversation is a single visitor session with your AI Twin — regardless of how many messages are exchanged. A session ends after 30 minutes of inactivity or when the visitor explicitly closes the widget. If the same visitor returns the next day, that is a new conversation.",
      },

      { type: "h2", text: "What does not count" },
      {
        type: "ul",
        items: [
          "Widget loads without any message sent — a browser opening the page but never chatting is free.",
          "Bot and crawler traffic — filtered automatically using user-agent and behavioural heuristics.",
          "Test conversations from your own admin dashboard — the Test panel is unmetered.",
          "Duplicate sessions from the same visitor within 30 minutes — treated as one.",
        ],
      },

      { type: "h2", text: "How to see your usage" },
      {
        type: "p",
        text: "Settings → Billing → Usage. The dashboard shows daily and monthly usage with a projection for the current period, so you can spot ramp-ups before they hit the cap.",
      },

      { type: "h2", text: "Overages" },
      {
        type: "p",
        text: "You'll get an email at 80% and again at 100% of your monthly quota. Above 100%:",
      },
      {
        type: "ul",
        items: [
          "Default — the twin keeps answering. Overage is billed at the per-conversation rate on your plan and invoiced at month end.",
          'Hard cap — turn overages off under Settings → Billing. Above 100%, the widget shows a soft "we\'ll be back" message.',
        ],
      },
      {
        type: "callout",
        kind: "info",
        text: "We recommend leaving overages on for revenue-critical sites. Missing a lead because you hit a soft cap is expensive.",
      },

      { type: "h2", text: "Choosing the right plan" },
      {
        type: "ul",
        items: [
          "Small marketing site — Free or Starter.",
          "Product site with active blog — Starter.",
          "SaaS with active support — Pro.",
          "High-traffic ecommerce — Pro or Enterprise.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Are handoff conversations counted?" },
      {
        type: "p",
        text: "Yes — a handoff is one conversation regardless of how long the human takes over.",
      },
      { type: "h3", text: "Do multi-twin conversations count once or per twin?" },
      {
        type: "p",
        text: "Per twin. If a visitor talks to two twins on the same site, that's two conversations.",
      },
      { type: "h3", text: "Can I roll unused conversations to next month?" },
      { type: "p", text: "No. Conversations reset each billing period." },
    ],
  },
  {
    slug: "change-plan",
    category: "Billing & plans",
    title: "Change, downgrade or cancel your Reality Twin plan",
    summary:
      "Upgrade instantly, downgrade at period end, cancel any time — everything is self-serve and your data stays exportable.",
    updated: "2026-06-06",
    readMinutes: 4,
    body: [
      {
        type: "p",
        text: "Reality Twin billing is designed to be low-drama. Upgrades, downgrades and cancellations are self-serve from Settings → Billing. Your data stays yours, exportable for 30 days after cancellation.",
      },

      { type: "h2", text: "Upgrade" },
      {
        type: "ol",
        items: [
          "Settings → Billing → Change plan.",
          "Pick the new plan and confirm.",
          "You're charged a prorated amount for the rest of the current period.",
          "New limits and features are active immediately.",
        ],
      },

      { type: "h2", text: "Downgrade" },
      {
        type: "p",
        text: "Downgrades take effect at the end of the current billing period so you keep what you've paid for. If your usage currently exceeds the lower plan's limits, we warn you before the switch — you can either bring usage down first or reconsider.",
      },

      { type: "h2", text: "Cancel" },
      { type: "p", text: "Cancel anytime from Settings → Billing." },
      {
        type: "ul",
        items: [
          "Your twin keeps running until the end of the current period.",
          "After period end, the workspace switches to read-only.",
          "Data stays available for export for 30 days.",
          "After 30 days, the workspace and all data are permanently deleted.",
        ],
      },

      { type: "h2", text: "Reactivate a cancelled workspace" },
      {
        type: "p",
        text: "Within the 30-day export window, click Reactivate from the dashboard banner. Everything resumes exactly as it was — knowledge, personality, conversations, integrations.",
      },

      { type: "h2", text: "Invoices and receipts" },
      {
        type: "p",
        text: "Every invoice is available under Settings → Billing → Invoices. Add a company name, VAT number and billing email under Settings → Billing → Company details — future invoices use those automatically.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Do you offer refunds?" },
      {
        type: "p",
        text: "Yes — a pro-rata refund for unused time on annual plans within 30 days of the charge. Monthly plans are not refunded but cancellation stops the next charge.",
      },
      { type: "h3", text: "How do I change the billing email?" },
      { type: "p", text: "Settings → Billing → Company details." },
      { type: "h3", text: "Can I pay by wire transfer / invoice?" },
      { type: "p", text: "Yes on Enterprise and annual Pro." },
    ],
  },

  // ── Security & privacy ─────────────────────────────────────────
  {
    slug: "data-residency",
    category: "Security & privacy",
    title: "Where is my Reality Twin data stored?",
    summary:
      "Regions, sub-processors, model inference routing, encryption — everything about where your data lives.",
    updated: "2026-06-05",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Reality Twin is built for European teams that care about data residency. This article covers exactly where your data lives, how it moves, and how to pin it to a specific region.",
      },

      { type: "h2", text: "Default region" },
      {
        type: "p",
        text: "Application data (knowledge sources, conversations, workspace metadata, embeddings) is stored in the European Union — Ireland. Reality Twin is a Swiss company operating under Swiss law, so the operator is Swiss while the storage is EU; both the GDPR and the Swiss FADP apply, and a DPA is available.",
      },

      { type: "h2", text: "Encryption" },
      {
        type: "ul",
        items: [
          "In transit — TLS 1.3, HSTS enforced.",
          "At rest — AES-256, keys managed by the cloud provider's KMS.",
          "Backups — encrypted with a separate key.",
          "Optional customer-managed keys (CMEK) on Enterprise.",
        ],
      },

      { type: "h2", text: "Model inference routing" },
      {
        type: "p",
        text: "Foundation-model inference happens with EU-hosted providers by default. US routing is opt-in per workspace and disabled unless you explicitly enable it. Enterprise plans can pin inference to Switzerland-hosted models.",
      },

      { type: "h2", text: "Sub-processors" },
      {
        type: "p",
        text: "The current sub-processor list is on /dpa. We notify DPA signatories in advance of any new sub-processor via email; you have the right to object.",
      },

      { type: "h2", text: "Isolation" },
      {
        type: "ul",
        items: [
          "Every workspace is logically isolated at the row-level in the database.",
          "Row-level security policies enforce workspace boundaries at query time.",
          "Enterprise plans can request physical isolation on dedicated infrastructure.",
        ],
      },

      { type: "h2", text: "Backups and recovery" },
      {
        type: "ul",
        items: [
          "Point-in-time recovery for the last 14 days.",
          "Daily snapshots kept for 30 days.",
          "Restoration RTO 4 hours, RPO 15 minutes.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Is my data ever used to train shared models?" },
      {
        type: "p",
        text: "No. Your data stays in your workspace and is never used for cross-customer training.",
      },
      { type: "h3", text: "Where is inference physically performed?" },
      { type: "p", text: "In the EU by default. Providers used are listed on /dpa." },
      { type: "h3", text: "Can I request a security review?" },
      { type: "p", text: "Yes — email hello@realitytwin.io for the full security package." },
    ],
  },
  {
    slug: "gdpr-fadp",
    category: "Security & privacy",
    title: "GDPR and Swiss FADP alignment",
    summary:
      "How Reality Twin handles your — and your visitors' — data rights under EU GDPR and Swiss FADP, with a working data-subject request workflow.",
    updated: "2026-06-04",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Reality Twin is aligned with the EU General Data Protection Regulation (GDPR) and the Swiss Federal Act on Data Protection (FADP). This article covers the roles, the rights, and exactly how to satisfy a data-subject request from your dashboard.",
      },

      { type: "h2", text: "Roles" },
      {
        type: "ul",
        items: [
          "You are the controller of your visitors' personal data.",
          "Reality Twin is your processor.",
          "The relationship is governed by our standard Data Processing Addendum (see /dpa) which we countersign on request.",
        ],
      },

      { type: "h2", text: "Data-subject rights we help you meet" },
      {
        type: "ul",
        items: [
          "Access — export all data tied to a person.",
          "Rectification — correct data you hold about them.",
          "Erasure — delete data on request, plus scheduled deletion after retention expires.",
          "Restriction — mark data as restricted pending resolution.",
          "Portability — export in a machine-readable format (JSON).",
          "Objection — stop processing on request.",
        ],
      },

      { type: "h2", text: "How to satisfy a request" },
      {
        type: "ol",
        items: [
          "Open Settings → Data → Personal data.",
          "Search by the person's email.",
          "Review the returned records (conversations, leads, uploaded content mentioning them).",
          "Export or delete in one click.",
          "The action is logged for your audit trail.",
        ],
      },

      { type: "h2", text: "Cross-border transfers" },
      {
        type: "p",
        text: "Where data must leave the EU (rare — see Where is my data stored?), transfers use Standard Contractual Clauses (SCCs) plus supplementary technical measures: TLS 1.3 in transit, AES-256 at rest, and access minimisation.",
      },

      { type: "h2", text: "Records of processing" },
      {
        type: "p",
        text: "Reality Twin maintains records of processing activities under Article 30 GDPR. On Enterprise plans we provide a customer-specific processing record on request.",
      },

      { type: "h2", text: "Certifications and audits" },
      {
        type: "callout",
        kind: "info",
        text: "SOC 2 Type I and ISO 27001 are on the roadmap. We will say so honestly on /security the day they are actually in place — not before.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Do you offer a DPA?" },
      { type: "p", text: "Yes — see Sign a DPA. Countersigned within two business days." },
      { type: "h3", text: "Where is the sub-processor list?" },
      { type: "p", text: "On /dpa. DPA signatories are notified in advance of any change." },
      { type: "h3", text: "How do I report a data incident?" },
      {
        type: "p",
        text: "Email hello@realitytwin.io. We acknowledge within one business day and follow the notification timelines in the DPA.",
      },
    ],
  },
  {
    slug: "sign-dpa",
    category: "Security & privacy",
    title: "Sign a DPA (Data Processing Addendum) with Reality Twin",
    summary:
      "Get a countersigned DPA in two business days — standard clauses, plain-language sub-processor list, SCC-backed transfers.",
    updated: "2026-06-03",
    readMinutes: 4,
    body: [
      {
        type: "p",
        text: "A Data Processing Addendum (DPA) formalises Reality Twin's role as your processor under GDPR and Swiss FADP. Signing one is a standard step for any B2B customer subject to privacy law.",
      },

      { type: "h2", text: "How to request one" },
      {
        type: "ol",
        items: [
          "Email hello@realitytwin.io with your legal entity name, registered address and the signer's details.",
          "We reply with our standard DPA as a PDF within one business day.",
          "You return a signed copy; we countersign and send back within one business day of receipt.",
        ],
      },
      { type: "p", text: "The entire round-trip is typically under two business days." },

      { type: "h2", text: "What the DPA covers" },
      {
        type: "ul",
        items: [
          "Roles (you as controller, us as processor).",
          "Purposes and duration of processing.",
          "Technical and organisational security measures.",
          "Sub-processor list and notification of changes.",
          "International data transfers (SCCs).",
          "Data-subject request assistance.",
          "Breach notification timelines.",
          "Return or deletion at the end of the relationship.",
        ],
      },

      { type: "h2", text: "Sub-processor list" },
      {
        type: "p",
        text: "The current sub-processor list is on /dpa. Signatories are notified by email at least 30 days before any change; you can object.",
      },

      { type: "h2", text: "Custom clauses" },
      {
        type: "p",
        text: "For most Starter and Pro customers, our standard DPA is the fastest path. Enterprise customers can request specific clauses (audit rights, additional notice periods, specific regions) — turnaround increases to 5–10 business days for redlines.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Is a DPA required?" },
      {
        type: "p",
        text: "If you process personal data of EU or Swiss residents through Reality Twin, yes.",
      },
      { type: "h3", text: "Is there a fee?" },
      { type: "p", text: "No — DPA signing is included on every plan." },
      { type: "h3", text: "Can I sign digitally?" },
      {
        type: "p",
        text: "Yes — DocuSign, Adobe Sign or a qualified electronic signature are all accepted.",
      },
    ],
  },
  {
    slug: "report-vulnerability",
    category: "Security & privacy",
    title: "Report a security vulnerability (responsible disclosure)",
    summary:
      "How to report a security issue to Reality Twin, our response timelines, and how researchers get credit.",
    updated: "2026-06-02",
    readMinutes: 5,
    body: [
      {
        type: "p",
        text: "We take security seriously and welcome responsible disclosure from independent researchers. This article covers scope, how to report, what to expect, and public credit.",
      },

      { type: "h2", text: "How to report" },
      {
        type: "ol",
        items: [
          "Email hello@realitytwin.io with a clear description, reproduction steps and expected impact.",
          "For sensitive reports, encrypt with our PGP key (available on request).",
          "Do not test on other customers' workspaces.",
          "Do not exfiltrate real data — a proof-of-concept in your own workspace is fine.",
        ],
      },

      { type: "h2", text: "What to expect" },
      {
        type: "ul",
        items: [
          "Acknowledgement within one business day.",
          "Triage and severity assessment within 3 business days.",
          "Remediation timeline shared within 5 business days.",
          "A follow-up when the fix is deployed.",
          "Public credit on /security with your consent.",
        ],
      },

      { type: "h2", text: "In scope" },
      {
        type: "ul",
        items: [
          "realitytwin.io and all *.realitytwin.io subdomains.",
          "The chat widget served from api/public/embed.",
          "The public REST API.",
          "The Reality Twin marketplace and profile pages.",
        ],
      },

      { type: "h2", text: "Out of scope" },
      {
        type: "ul",
        items: [
          "Social engineering of employees or customers.",
          "Physical attacks against our offices or cloud providers.",
          "Denial-of-service attacks against production.",
          "Findings that require a stolen device or a rooted phone.",
          "Reports from automated scanners without proof of exploitability.",
        ],
      },

      { type: "h2", text: "Rewards" },
      {
        type: "p",
        text: "We don't run a public bounty programme yet. For severe issues we offer swag, credits, and — with permission — a public thank-you on /security.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Do you have a security.txt?" },
      { type: "p", text: "Yes — at /.well-known/security.txt." },
      { type: "h3", text: "Can I disclose publicly after remediation?" },
      {
        type: "p",
        text: "Yes, and we encourage it. Coordinate the date with us so we can prepare a joint communication.",
      },
    ],
  },

  // ── Troubleshooting ────────────────────────────────────────────
  {
    slug: "widget-not-appearing",
    category: "Troubleshooting",
    title: "AI Twin chat widget not appearing on my site",
    summary:
      "The complete checklist when your Reality Twin widget doesn't load — ad-blockers, CSP, allow-lists, script tag, past-due status.",
    updated: "2026-06-01",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "Nine times out of ten a missing chat widget is one of five specific issues. Walk this checklist in order — one of them will fix it.",
      },

      { type: "h2", text: "1. Confirm the script tag is on the page" },
      {
        type: "ol",
        items: [
          "Open the page in a private browser window.",
          "View page source (Cmd/Ctrl+U) and search for realitytwin.io.",
          "You should see one <script> tag with your Company ID.",
          "If not, the snippet was not saved — re-paste it and republish.",
        ],
      },

      { type: "h2", text: "2. Check the browser console" },
      {
        type: "p",
        text: "Open DevTools → Console. Look for errors mentioning realitytwin.io. Two common ones:",
      },
      {
        type: "ul",
        items: [
          "Content Security Policy — see step 3.",
          "CORS — add your domain to the allow-list (step 4).",
        ],
      },

      { type: "h2", text: "3. Update your Content Security Policy" },
      { type: "p", text: "If your site sends a CSP header, add these directives:" },
      {
        type: "code",
        lang: "text",
        text: `script-src 'self' https://realitytwin.io;
connect-src 'self' https://realitytwin.io wss://realitytwin.io;
frame-src https://realitytwin.io;
img-src 'self' https://realitytwin.io data:;`,
      },

      { type: "h2", text: "4. Confirm your domain is allow-listed" },
      {
        type: "p",
        text: "Twin → Settings → Domains. The widget refuses to render on domains not on your allow-list — this stops scraped copies of your site from showing your widget.",
      },

      { type: "h2", text: "5. Check for ad-blockers" },
      {
        type: "p",
        text: "uBlock, Ghostery and Brave's shields sometimes block third-party widgets by default. Temporarily disable in a private window to confirm this is the cause. In production, communicate the widget's value to users so they whitelist it.",
      },

      { type: "h2", text: "6. Verify your account is active" },
      {
        type: "p",
        text: "The widget disables itself if the account is past due or suspended. Check Settings → Billing.",
      },

      { type: "h2", text: "Still not working?" },
      {
        type: "p",
        text: "Email hello@realitytwin.io with the page URL and a screenshot of the browser console. We usually reply within 4 business hours on Pro.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Does the widget work behind a corporate proxy?" },
      {
        type: "p",
        text: "Yes — as long as the proxy allows outbound HTTPS and WebSocket to realitytwin.io.",
      },
      { type: "h3", text: "Can I load the widget on a subdomain not in the allow-list?" },
      { type: "p", text: "No — add the subdomain explicitly." },
      { type: "h3", text: "Why does the widget appear in staging but not in production?" },
      {
        type: "p",
        text: "Usually a stricter production CSP or a different domain not on the allow-list.",
      },
    ],
  },
  {
    slug: "twin-missing-answer",
    category: "Troubleshooting",
    title: "AI Twin says it can't find something it should know",
    summary:
      "Three root causes when your AI Twin has the knowledge but doesn't surface it — and how to fix each one.",
    updated: "2026-05-30",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: 'When your AI Twin says "I don\'t know" about something you know it has been fed, one of three things is almost always happening. This article covers each cause with a specific fix.',
      },

      { type: "h2", text: "Cause 1 — The source wasn't indexed" },
      { type: "p", text: "Open Knowledge → Sources and find the page or document." },
      {
        type: "ul",
        items: [
          "Status Indexed — the source is in the graph; skip to cause 2.",
          "Status Failed — see Website crawl failed or is incomplete.",
          "Missing entirely — re-crawl, re-upload, or add a sitemap.xml.",
        ],
      },

      { type: "h2", text: "Cause 2 — The phrasing doesn't match" },
      {
        type: "p",
        text: "Reality Twin retrieves by semantic similarity, not keyword. If your source uses very different vocabulary from the visitor's question, retrieval can miss.",
      },
      { type: "h3", text: "Fix" },
      {
        type: "ul",
        items: [
          "Add a structured Q&A that uses the visitor's vocabulary — this bridges the gap without changing the source.",
          "Or edit the source to include the visitor's phrasing in a synonyms sentence.",
        ],
      },

      { type: "h2", text: "Cause 3 — A boundary rule is silencing it" },
      {
        type: "p",
        text: "Open Twin → Personality → Boundaries. A rule may be intercepting the topic and forcing a refusal.",
      },
      { type: "h3", text: "Fix" },
      {
        type: "ul",
        items: [
          "Narrow the rule with more specific example phrasings.",
          "Or delete the rule if it was too broad.",
        ],
      },

      { type: "h2", text: "Diagnostic tip: use the test panel with citations" },
      {
        type: "p",
        text: "The Twin → Test panel shows retrieval scores for every candidate chunk. If the right chunk has a low score, you have a phrasing problem (cause 2). If it isn't in the list at all, you have an indexing problem (cause 1).",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Why did an answer regress after re-crawling?" },
      {
        type: "p",
        text: "Usually a source page was updated with different wording. Add a Q&A to lock the answer in.",
      },
      { type: "h3", text: "Does capitalisation matter?" },
      { type: "p", text: "No — retrieval is case-insensitive." },
      { type: "h3", text: "Can I force the twin to use a specific source?" },
      {
        type: "p",
        text: "Yes — mark the source High priority under Knowledge → Sources → source detail.",
      },
    ],
  },
  {
    slug: "crawl-failed",
    category: "Troubleshooting",
    title: "Website crawl failed or is incomplete",
    summary:
      "Common causes of failed or partial Reality Twin crawls — robots.txt, bot protection, JavaScript, orphan pages — and how to unblock each.",
    updated: "2026-05-28",
    readMinutes: 6,
    body: [
      {
        type: "p",
        text: "A failed or partial crawl means your AI Twin is starting life with an incomplete knowledge graph. Walk this list to identify why the crawler didn't get everything, then fix and re-crawl.",
      },

      { type: "h2", text: "1. robots.txt blocks the crawler" },
      {
        type: "p",
        text: "Fetch https://your-site.com/robots.txt and look for Disallow rules that apply to user-agent * or RealityTwinBot. Fix:",
      },
      {
        type: "code",
        lang: "text",
        text: `User-agent: RealityTwinBot
Allow: /`,
      },

      { type: "h2", text: "2. Bot protection (Cloudflare, WAF, DataDome)" },
      { type: "p", text: "Managed challenge or bot fight may block automated fetches. Fix:" },
      {
        type: "ul",
        items: [
          "Allow-list our crawler IP range — email hello@realitytwin.io for the current range.",
          "Add RealityTwinBot to your bot-management verified-bot list.",
          'In Cloudflare, create a WAF rule: (http.user_agent contains "RealityTwinBot") allow.',
        ],
      },

      { type: "h2", text: "3. JavaScript-only content" },
      {
        type: "p",
        text: "The crawler renders JavaScript with a 10-second budget per page. If content only appears after a long client-side fetch, it may be missed. Fix:",
      },
      {
        type: "ul",
        items: [
          "Publish a static HTML export of critical pages.",
          "Add server-side rendering (SSR) or pre-rendering for indexable pages.",
          "Or upload the content as a document.",
        ],
      },

      { type: "h2", text: "4. Orphan pages" },
      {
        type: "p",
        text: "The crawler follows internal links from the root. Pages not linked from anywhere will be missed. Fix:",
      },
      {
        type: "ul",
        items: [
          "Link to them from a sitemap page or footer.",
          "Or submit a sitemap.xml under Knowledge → Sources → Add sitemap.",
        ],
      },

      { type: "h2", text: "5. Rate-limited (429 responses)" },
      {
        type: "p",
        text: "The crawler respects Retry-After. If your site aggressively rate-limits, the crawl slows to a crawl. Fix:",
      },
      {
        type: "ul",
        items: [
          "Raise the limit for the RealityTwinBot user-agent.",
          "Or allow-list the crawler IP range.",
        ],
      },

      { type: "h2", text: "6. Very large sites without a sitemap" },
      { type: "p", text: "The crawler will stop at your plan's page cap. Fix:" },
      {
        type: "ul",
        items: [
          "Submit a sitemap so we prioritise the most important pages.",
          "Upgrade to a plan with a higher cap.",
        ],
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "How do I retry a failed crawl?" },
      {
        type: "p",
        text: "Knowledge → Sources → your website → Re-crawl. Fix the root cause first.",
      },
      { type: "h3", text: "Where do I see which pages failed?" },
      {
        type: "p",
        text: "Knowledge → Sources → your website → Failed pages. Each row shows the HTTP status and reason.",
      },
      { type: "h3", text: "Can I contact support to diagnose?" },
      {
        type: "p",
        text: "Yes — hello@realitytwin.io with your site URL. We can inspect logs on our side.",
      },
    ],
  },
  {
    slug: "signin-issues",
    category: "Troubleshooting",
    title: "Sign-in and password reset issues",
    summary:
      "Locked out of Reality Twin, magic link not arriving, or Google sign-in looping — walk through it here.",
    updated: "2026-05-25",
    readMinutes: 5,
    body: [
      { type: "p", text: "Most sign-in issues are one of four things. Walk this list in order." },

      { type: "h2", text: "1. Password reset email not arriving" },
      {
        type: "ul",
        items: [
          "Check spam, promotions and clutter folders.",
          "Corporate mail filters sometimes hold new senders — ask IT to allow-list @realitytwin.io.",
          "Reset requests older than 60 minutes expire — request a new one at /forgot-password.",
          "Confirm you're using the same email as your workspace.",
        ],
      },

      { type: "h2", text: "2. Google sign-in redirects in a loop" },
      { type: "p", text: "Almost always a third-party cookie block. Fix:" },
      {
        type: "ul",
        items: [
          "Enable third-party cookies for realitytwin.io in your browser.",
          "Or try a different browser to confirm the diagnosis.",
          "Corporate networks: ask IT to allow oauth flows to realitytwin.io.",
        ],
      },

      { type: "h2", text: '3. "Unsupported provider" error' },
      {
        type: "p",
        text: "This usually means your workspace requires SSO and you tried password sign-in. Contact your workspace admin, or email hello@realitytwin.io — we'll route you to the correct auth flow.",
      },

      { type: "h2", text: "4. MFA / TOTP code rejected" },
      {
        type: "ul",
        items: [
          "Check that your device's time is set to automatic — TOTP is time-based.",
          "Try the code as soon as it appears; codes rotate every 30 seconds.",
          "If your authenticator is lost, contact a workspace admin to reset MFA.",
        ],
      },

      { type: "h2", text: "Recover a lost workspace" },
      {
        type: "p",
        text: "If you've lost all Admin access, email hello@realitytwin.io from a domain that matches the workspace. We verify ownership via DNS TXT record and reinstate access — typically same business day.",
      },

      { type: "h2", text: "Frequently asked questions" },
      { type: "h3", text: "Can I use a passkey?" },
      { type: "p", text: "Yes — passkeys are supported wherever password sign-in is." },
      { type: "h3", text: "How do I change my email?" },
      {
        type: "p",
        text: "Settings → Account → Change email. Confirm from both the old and new inbox.",
      },
      { type: "h3", text: "How long do sessions last?" },
      { type: "p", text: "30 days rolling. Sensitive actions re-authenticate." },
    ],
  },
];

export function getArticle(slug: string): SupportArticle | undefined {
  return SUPPORT_ARTICLES.find((a) => a.slug === slug);
}

export function getRelatedArticles(slug: string, limit = 3): SupportArticle[] {
  const article = getArticle(slug);
  if (!article) return [];
  return SUPPORT_ARTICLES.filter((a) => a.slug !== slug && a.category === article.category).slice(
    0,
    limit,
  );
}

export const SUPPORT_CATEGORIES = Array.from(new Set(SUPPORT_ARTICLES.map((a) => a.category)));
